import { Box } from '@mui/material';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router-dom';

import CardTable from 'common/components/CardTable/CardTable';
import { Loader } from 'common/components/Loader/Loader';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	MODULES,
	TABLE_VIEW_PAGE_SIZE,
	GRID_VIEW_PAGE_SIZE,
	ViewType,
	NO_DATA_FOUND,
	CARD_SIZE_12,
	TABLE_SIZE_10,
} from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	OBLIGATION_TABLE_DEFAULT_FILTER,
	TAXONOMY_TAB_VALUES,
} from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setQueryFlag,
	setRequirementInventoryBreadcrumbText,
	setRiskRefSelectedTab,
} from 'common/store/reducer/commonReducer';
import { setControlStepsData } from 'common/store/reducer/controlTestScriptReducer';
import { setTaxonomyDetailsTabIndex } from 'common/store/reducer/mappingDetailsReducer';
import { setRiskRefTaxonomySourceFiltersKeyFlag } from 'common/store/reducer/taxonomyReducer';
import { isModuleExists } from 'common/utils/authCheck';
import {
	scrollToTop,
	urlWithTenant,
	getGridPageSize,
	getTablePageSize,
	downloadDocAndSave,
	isModuleEnabled,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import useTableQuery from 'hooks/useTableQuery';

import { handleSpecialCharacters } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import { getTaxonomyRefTableData } from '../../api';
import {
	IRiskRefTableProps,
	RiskRefCardData,
	RiskRefTableData,
} from '../../api/type-def';

import {
	getApiData,
	getDefaultRiskTableQueryParams,
	getDefaultSortKey,
	getNavigateLink,
	getRiskRefTaxonomyColumn,
	getTableQuery,
} from '../RiskRefTaxonomyUtils';

import RisksTaxonomyCardView from './RisksTaxonomyCardView';

import styles from './RisksTaxonomyTable.module.scss';

const {
	CITATIONS_VALUE,

	OBLIGATIONS_VALUE,
	POLICIES_VALUE,
} = TAXONOMY_TAB_VALUES;

const { RISK_REFERENCE_TAXONOMY, REQUIREMENT_INVENTORY } = MODULES;

const { OK, NOT_FOUND } = HTTP_STATUS_CODES;
const { TABLE_SIZE_12, TABLE_SIZE_24, TABLE_SIZE_48 } = TABLE_VIEW_PAGE_SIZE;
const { GRID_SIZE_10, GRID_SIZE_25, GRID_SIZE_50 } = GRID_VIEW_PAGE_SIZE;
const { CARD, TABLE } = ViewType;
const RisksTaxonomyTable = (props: IRiskRefTableProps) => {
	const { tableType, isClearFilterValues } = props;

	const tableContainerStyles = { minWidth: '46.875em', maxHeight: '27.5em' };
	const isRequirementInventoryEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_INVENTORY_ENABLED,
	);
	const isRequirementInventoryModuleExists = isModuleEnabled(
		isRequirementInventoryEnabled,
		isModuleExists(REQUIREMENT_INVENTORY),
	);
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const indexType = useSelector(
		(state: RootState) => state.common.selectedTabIndex,
	);

	const tableIndex = useSelector(
		(state: RootState) => state.common.selectedTabName,
	);

	const searchText = useSelector(
		(state: RootState) => state.taxonomy.riskRefSearchText,
	);

	const refFilterData = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomyFilters,
	);
	const applyFilterFlag = useSelector(
		(state: RootState) => state.taxonomy.applyFilterFlag,
	);
	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);

	const riskRefTaxonomySourceFiltersKeyFlag = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKeyFlag,
	);

	const queryFlag = useSelector(
		(appState: RootState) => appState.common.tableQueryFlag,
	);
	const taxonomyViewType = useSelector(
		(state: RootState) => state.taxonomy.taxonomyViewTypeValue,
	);
	const [riskRefTableData, setRiskRefTableData] = useState<RiskRefTableData>(
		[],
	);
	const [toastMessage, setToastMessageLocal] = useState('');
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState(0);
	const [cardPageSize, setCardPageSize] = useState<number>(12);
	const defaultTableSize =
		taxonomyViewType === CARD ? CARD_SIZE_12 : TABLE_SIZE_10;
	const obligationTableQuery = useTableQuery({
		id: 'RiskRefObligationTable',
		defaultPageSize: defaultTableSize,
	});

	const citationTableQuery = useTableQuery({
		id: 'RiskRefCitationTable',
		defaultPageSize: defaultTableSize,
	});

	const processTableQuery = useTableQuery({
		id: 'RiskRefProcessTable',
		defaultPageSize: defaultTableSize,
	});

	const riskTableQuery = useTableQuery({
		id: 'RiskRefRiskTable',
		defaultPageSize: defaultTableSize,
	});

	const controlTableQuery = useTableQuery({
		id: 'RiskRefControlTable',
		defaultPageSize: defaultTableSize,
	});
	const { updateTableQuery: regInvRelationshipUpdateTable } = useTableQuery({
		id: 'regInvRelationshipCard',
	});
	const { updateTableQuery: updateRelationCardTableQuery } = useTableQuery({
		id: 'prcRelationshipMappingCard',
		defaultPageSize: TABLE_SIZE_12,
	});
	const resetRiskRefTableIndex = () => {
		dispatch(setRiskRefSelectedTab({ tableIndex, indexType }));
	};

	const navigateToDetailPage = async (
		params: any,
		category: TAXONOMY_TAB_VALUES,
	) => {
		dispatch(setControlStepsData([]));
		if (category === POLICIES_VALUE) {
			downloadDocAndSave(params?.fileName);
		} else {
			updateQueryFlag(true);
			updateRelationCardTableQuery({
				pageNumber: 0,
				pageSize: TABLE_SIZE_12,
			});
			resetRiskRefTableIndex();
			dispatch(setTaxonomyDetailsTabIndex('overview'));

			if (category === OBLIGATIONS_VALUE) {
				if (isRequirementInventoryModuleExists) {
					regInvRelationshipUpdateTable({
						pageNumber: 0,
						pageSize: TABLE_SIZE_12,
					});
					dispatch(
						setRequirementInventoryBreadcrumbText(RISK_REFERENCE_TAXONOMY),
					);
				} else {
					navigate(urlWithTenant('/unauthorized'));
				}
			}

			const navigateLink = getNavigateLink(
				params,
				category,
				location?.pathname,
			);
			navigate(urlWithTenant(navigateLink));
		}
	};

	const fetchRisksTableData = async (data: any) => {
		if (riskRefTaxonomySourceFiltersKey === '') return;
		setLoading(true);
		setRiskRefTableData([]);
		const apiData = getApiData(data, tableType);
		dispatch(setTaxonomyDetailsTabIndex('overview'));
		const updatedSearchString = handleSpecialCharacters(searchText);
		const filter =
			tableType === OBLIGATIONS_VALUE
				? [...refFilterData, ...OBLIGATION_TABLE_DEFAULT_FILTER]
				: refFilterData;
		const paramsObj = {
			searchText: updatedSearchString,
			index: tableIndex,
			filter,
			source: riskRefTaxonomySourceFiltersKey,
		};
		const resp = await getTaxonomyRefTableData(paramsObj, apiData);
		if (resp?.statusCode === OK) {
			const responseData = resp?.responseBody;
			const tempTableData =
				responseData?.content && responseData?.content?.length
					? responseData?.content
					: [];
			if (tableType === CITATIONS_VALUE && tempTableData.length > 0) {
				const updatedData = tempTableData?.map((item: any) => ({
					...item,
					citationTitle: item?.paragraphId,
					citationId: item?.id,
					id: item?.regulationId,
				}));
				setRiskRefTableData(updatedData);
			} else {
				setRiskRefTableData(tempTableData);
			}
			setTotalRecordCount(responseData?.totalCount || 0);
			setTotalPagesCount(responseData?.totalPages || 0);
			setLoading(false);
		} else if (resp?.statusCode === NOT_FOUND) {
			setIsToastOpen(true);
			setToastMessageLocal('No Data Found');
			setLoading(false);
			setRiskRefTableData([]);
		} else {
			setIsToastOpen(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setLoading(false);
			setRiskRefTableData([]);
		}
	};
	const [, setPageChange] = useState<any>();
	const columns = useMemo(
		() =>
			getRiskRefTaxonomyColumn(
				tableType,
				navigateToDetailPage,
				riskRefTaxonomySourceFiltersKey,
				isRequirementInventoryModuleExists,
			),
		[tableType, riskRefTaxonomySourceFiltersKey],
	);

	const { tableQueryObj, updateTableQuery } = getTableQuery(
		obligationTableQuery,
		citationTableQuery,
		processTableQuery,
		riskTableQuery,
		controlTableQuery,
		tableType,
	);

	const updateQueryFlag = (val: boolean) => {
		dispatch(setQueryFlag(val));
	};

	const handlePageChange = (data: any, flag?: boolean) => {
		if (taxonomyViewType === TABLE && flag) {
			updateTableQuery(data);
			fetchRisksTableData(data);
		}
	};

	const handleCardPageChange = (data: any) => {
		if (taxonomyViewType === CARD) {
			updateTableQuery(data);
			fetchRisksTableData(data);
		}
	};
	const handleSortChange = (data: any) => {
		fetchRisksTableData(data);
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	const defaultSortKey = useMemo(
		() => getDefaultSortKey(tableType),
		[tableType],
	);

	const reloadRiskTableData = () => {
		const defaultRiskTableQueryParams = getDefaultRiskTableQueryParams(
			tableType,
			taxonomyViewType,
		);

		updateTableQuery(defaultRiskTableQueryParams);
		setLoading(true);
		fetchRisksTableData(defaultRiskTableQueryParams);
	};

	useEffect(() => {
		if (applyFilterFlag) {
			reloadRiskTableData();
		}
	}, [applyFilterFlag]);

	useEffect(() => {
		if (riskRefTaxonomySourceFiltersKeyFlag) {
			setRiskRefTableData([]);
			reloadRiskTableData();
			dispatch(setRiskRefTaxonomySourceFiltersKeyFlag(false));
		}
	}, [riskRefTaxonomySourceFiltersKey]);

	useEffect(() => {
		if (isClearFilterValues) {
			setRiskRefTableData([]);
			reloadRiskTableData();
		}
	}, [isClearFilterValues]);
	useEffect(() => {
		setLoading(true);
		fetchRisksTableData(tableQueryObj);
	}, [tableQueryObj]);
	useEffect(() => {
		setRiskRefTableData([]);
		const paramsData = { ...tableQueryObj };
		if (queryFlag) {
			resetPaginationBasedOnType(paramsData);
			updateQueryFlag(false);
		} else if (searchText) {
			setLoading(true);
			reloadRiskTableData();
		} else {
			resetPaginationBasedOnType(paramsData);
		}
	}, [searchText, taxonomyViewType]);

	useEffect(() => {
		if (tableType === OBLIGATIONS_VALUE) scrollToTop();
	}, []);
	const resetPaginationBasedOnType = (paramsData?: any) => {
		const pageInfo = { ...paramsData };
		if (taxonomyViewType === CARD) {
			if (
				tableQueryObj?.pageSize === TABLE_SIZE_12 ||
				tableQueryObj?.pageSize === TABLE_SIZE_24 ||
				tableQueryObj?.pageSize === TABLE_SIZE_48
			) {
				setCardPageSize(pageInfo?.pageSize);
				fetchRisksTableData(pageInfo);
			} else {
				const size = getTablePageSize(pageInfo?.pageSize);
				pageInfo.pageSize = size;
				setCardPageSize(size);
				fetchRisksTableData(pageInfo);
			}

			updateTableQuery(pageInfo);
		} else {
			if (
				tableQueryObj?.pageSize === GRID_SIZE_10 ||
				tableQueryObj?.pageSize === GRID_SIZE_25 ||
				tableQueryObj?.pageSize === GRID_SIZE_50
			) {
				setPageChange(pageInfo?.pageSize);
			} else {
				const size = getGridPageSize(pageInfo?.pageSize);
				pageInfo.pageSize = size;
				setPageChange(pageInfo);
			}
			updateTableQuery(pageInfo);
		}
	};

	const renderTableComponent = () => (
		<TableComponent
			defaultQuery={tableQueryObj}
			rows={riskRefTableData}
			columns={columns}
			totalItems={totalRecordsCount}
			defaultSortKey={defaultSortKey}
			onPageHandler={handlePageChange}
			isLoading={isLoading}
			tableStyles={tableContainerStyles}
			totalPagesCount={totalPagesCount}
			onSortHandler={handleSortChange}
			enableHeaderColor
			isRenderPagination
		/>
	);

	const renderCardTable = () => (
		<CardTable
			id={tableType}
			handlePagination={handleCardPageChange}
			queryPageSize={cardPageSize}
			render={(rowData: RiskRefCardData) => (
				<RisksTaxonomyCardView
					data={rowData}
					tableType={tableType}
					navigateToDetailPage={navigateToDetailPage}
				/>
			)}
			rows={riskRefTableData}
			totalItems={totalRecordsCount}
			defaultQuery={tableQueryObj}
			columns={3}
			showSelectPageOption
		/>
	);

	const renderNoDataFound = () => <div>{NO_DATA_FOUND}</div>;

	const renderCardLoader = () => (
		<div>
			<Loader show={isLoading} />
		</div>
	);
	const renderCardContent = () => {
		if (isLoading) {
			return renderCardLoader();
		} else if (riskRefTableData && riskRefTableData.length > 0) {
			return renderCardTable();
		} else if (
			!isLoading &&
			(!riskRefTableData || riskRefTableData.length === 0)
		) {
			return renderNoDataFound();
		}
		return null;
	};
	const renderContent = () => {
		switch (taxonomyViewType) {
			case 'TABLE':
				return renderTableComponent();
			case 'CARD':
				return renderCardContent();
			default:
				if (!isLoading && riskRefTableData && riskRefTableData?.length > 0) {
					return renderCardTable();
				}
				return null;
		}
	};

	return (
		<Box className={styles['risk-ref-table-box']}>
			<>{renderContent()}</>
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default RisksTaxonomyTable;
